import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MessageSquare,
  Pencil,
  Receipt,
  Send,
  Trash2,
  Users,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { MoneyAmount } from "../components/MoneyAmount";
import { getInitials } from "../lib/initials";
import {
  useAddComment,
  useDeleteComment,
  useDeleteExpense,
  useExpense,
  useGroup,
  useUpdateComment,
} from "../api/hooks";
import type { Comment, Expense, Group } from "../api/types";

export function ExpenseDetailPage(): ReactNode {
  const { groupId, expenseId } = useParams<{
    groupId: string;
    expenseId: string;
  }>();
  const groupQ = useGroup(groupId);
  // Single-resource lookup that walks pagination if needed; ensures deep
  // links to expenses past the first page resolve correctly.
  const expenseQ = useExpense(groupId, expenseId);

  if (groupQ.isLoading || expenseQ.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-stack-lg">
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (!groupQ.data || !expenseQ.data) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          Expense not found.
        </CardContent>
      </Card>
    );
  }

  return <ExpenseDetail group={groupQ.data} expense={expenseQ.data} />;
}

function ExpenseDetail({
  group,
  expense,
}: {
  group: Group;
  expense: Expense;
}): ReactNode {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteExpense = useDeleteExpense(group.id);

  const total = expense.items.reduce((sum, item) => sum + item.cost, 0);
  const participantNames = new Map(
    group.participants.map((p) => [p.id, p.displayName]),
  );

  async function onDelete(): Promise<void> {
    await deleteExpense.mutateAsync(expense.id);
    setConfirmDelete(false);
    navigate(`/groups/${group.id}`);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-stack-lg">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(`/groups/${group.id}`)}
          className="inline-flex cursor-pointer items-center gap-2 text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
        >
          <ArrowLeft className="size-4" />
          Back
        </button>
        <div className="flex items-center gap-2">
          <Link to={`/groups/${group.id}/expenses/${expense.id}/edit`}>
            <Button variant="ghost" size="sm">
              <Pencil className="size-4" />
              Edit
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-error-container hover:text-on-error-container"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="size-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Hero — high-impact summary banner */}
      <section className="relative isolate overflow-hidden rounded-lg bg-primary p-8 text-on-primary [clip-path:inset(0_round_2rem)]">
        <div className="relative z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="mb-3 inline-block rounded-full bg-on-primary/15 px-3 py-1 text-label-sm">
                Expense
              </span>
              <h1 className="truncate text-headline-lg">{expense.label}</h1>
              <p className="mt-1 text-label-md text-on-primary/70">
                {new Date(expense.createdAt).toLocaleString()}
              </p>
            </div>
            <div className="grid size-12 shrink-0 place-items-center rounded-md bg-on-primary/15 backdrop-blur-md">
              <Receipt className="size-6" />
            </div>
          </div>
          <div className="mt-8">
            <p className="text-label-sm text-on-primary/60">Total amount</p>
            <MoneyAmount
              minor={total}
              currency={expense.currency}
              className="text-display"
            />
            {expense.currency !== group.currency ? (
              <p className="mt-1 text-label-md text-on-primary/70">
                <MoneyAmount
                  minor={expense.groupCharge}
                  currency={group.currency}
                />{" "}
                in group currency
              </p>
            ) : null}
          </div>
        </div>
        {/* Decorative atmospheric glow */}
        <div className="pointer-events-none absolute -right-12 -top-16 size-48 rounded-full bg-on-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-12 size-48 rounded-full bg-primary-container/40 blur-3xl" />
      </section>

      <section className="space-y-stack-md">
        <div className="flex items-center justify-between">
          <h2 className="text-headline-md">Paid by</h2>
          <span className="text-label-sm text-on-surface-variant">
            {expense.payments.length} payer
            {expense.payments.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="space-y-stack-sm">
          {expense.payments.map((p) => {
            const name = participantNames.get(p.participantId) ?? "Unknown";
            const pct = total > 0 ? Math.round((p.amount / total) * 100) : 0;
            return (
              <Card
                key={p.participantId}
                tone="ghost"
                className="border border-outline-variant/60 transition-colors hover:border-primary/50"
              >
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid size-11 shrink-0 place-items-center rounded-full bg-primary-fixed text-label-md text-on-primary-fixed-variant">
                      {getInitials(name) || <Users className="size-5" />}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-body-md font-bold">{name}</p>
                      <p className="text-label-sm text-on-surface-variant">
                        {pct === 100 ? "Paid full amount" : `${pct}% of total`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <MoneyAmount
                      minor={p.amount}
                      currency={expense.currency}
                      className="text-headline-md text-tertiary"
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="space-y-stack-md">
        <h2 className="text-headline-md">Items</h2>
        <Card
          tone="ghost"
          className="overflow-hidden border border-outline-variant/60"
        >
          <CardContent className="divide-y divide-outline-variant/30 p-0">
            {expense.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-4 p-4 transition-colors hover:bg-surface-container/50"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-primary-fixed text-primary">
                    <Receipt className="size-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-body-md font-bold">
                      {item.label}
                    </p>
                    <p className="text-label-sm text-on-surface-variant">
                      Split {item.split.type.toLowerCase()} across{" "}
                      {item.split.divisions.length} participant
                      {item.split.divisions.length === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <MoneyAmount
                  minor={item.cost}
                  currency={expense.currency}
                  className="shrink-0 text-headline-md"
                />
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      <CommentsSection group={group} expense={expense} />

      <Dialog
        open={confirmDelete}
        onOpenChange={(o) => (!o ? setConfirmDelete(false) : null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete expense?</DialogTitle>
            <DialogDescription>
              Removes "{expense.label}" and all its comments. This can't be
              undone.
            </DialogDescription>
          </DialogHeader>
          {deleteExpense.error ? (
            <p className="text-label-md text-error">
              Couldn't delete the expense.
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={deleteExpense.isPending}
            >
              {deleteExpense.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommentsSection({
  group,
  expense,
}: {
  group: Group;
  expense: Expense;
}): ReactNode {
  const addComment = useAddComment(group.id, expense.id);
  const [draft, setDraft] = useState("");

  async function onPost(event: FormEvent): Promise<void> {
    event.preventDefault();
    const message = draft.trim();
    if (!message) return;
    await addComment.mutateAsync({ message });
    setDraft("");
  }

  const participantNames = new Map(
    group.participants.map((p) => [p.id, p.displayName]),
  );

  return (
    <section className="space-y-stack-md">
      <div className="flex items-center gap-2">
        <MessageSquare className="size-5 text-on-surface-variant" />
        <h2 className="text-headline-md">Comments</h2>
      </div>

      {expense.comments.length === 0 ? (
        <Card tone="ghost" className="border border-outline-variant/60">
          <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
              <MessageSquare className="size-6" />
            </span>
            <div className="space-y-1">
              <p className="text-body-md text-on-surface-variant">
                No comments yet.
              </p>
              <p className="text-label-sm text-outline">
                Be the first to say something about this expense.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card tone="ghost" className="border border-outline-variant/60">
          <CardContent className="divide-y divide-outline-variant/30 p-0">
            {expense.comments.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                groupId={group.id}
                expenseId={expense.id}
                authorName={
                  c.createdBy === "SYSTEM"
                    ? "System"
                    : (participantNames.get(c.createdBy) ?? "Unknown")
                }
              />
            ))}
          </CardContent>
        </Card>
      )}

      <form className="flex items-end gap-2" onSubmit={onPost}>
        <div className="flex-1">
          <Label htmlFor="comment-draft" className="sr-only">
            Add a comment
          </Label>
          <Input
            id="comment-draft"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment…"
            maxLength={2000}
          />
        </div>
        <Button
          type="submit"
          size="icon"
          aria-label="Post comment"
          disabled={!draft.trim() || addComment.isPending}
        >
          <Send className="size-4" />
        </Button>
      </form>
      {addComment.error ? (
        <p className="text-label-md text-error">Couldn't post comment.</p>
      ) : null}
    </section>
  );
}

function CommentRow({
  comment,
  groupId,
  expenseId,
  authorName,
}: {
  comment: Comment;
  groupId: string;
  expenseId: string;
  authorName: string;
}): ReactNode {
  const updateComment = useUpdateComment(groupId, expenseId);
  const deleteComment = useDeleteComment(groupId, expenseId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.message);
  const isSystem = comment.createdBy === "SYSTEM";

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    const message = draft.trim();
    if (!message || message === comment.message) {
      setEditing(false);
      return;
    }
    await updateComment.mutateAsync({
      commentId: comment.id,
      body: { message },
    });
    setEditing(false);
  }

  async function onDelete(): Promise<void> {
    await deleteComment.mutateAsync(comment.id);
  }

  return (
    <div className="px-5 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-label-sm text-on-surface-variant">
          {authorName} · {new Date(comment.createdAt).toLocaleString()}
        </p>
        {!isSystem && !editing ? (
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Edit comment"
              onClick={() => {
                setDraft(comment.message);
                setEditing(true);
              }}
            >
              <Pencil className="size-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Delete comment"
              onClick={onDelete}
              disabled={deleteComment.isPending}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ) : null}
      </div>
      {editing ? (
        <form className="mt-2 flex items-end gap-2" onSubmit={onSave}>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            autoFocus
          />
          <Button type="submit" disabled={updateComment.isPending}>
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
        </form>
      ) : (
        <p className="mt-1 whitespace-pre-wrap text-body-md">
          {comment.message}
        </p>
      )}
      {deleteComment.error ? (
        <p className="mt-1 text-label-sm text-error">
          Couldn't delete comment.
        </p>
      ) : null}
    </div>
  );
}
